/**
 * Stripe API Schemas
 *
 * Zod schemas for Stripe-related API request/response validation.
 * SINGLE SOURCE OF TRUTH for Stripe API contract.
 */

import { z } from 'zod/v4';

// ============================================================================
// Helpers
// ============================================================================

// Origins the UI is allowed to hand Stripe as a post-checkout redirect target.
// Keep in sync with ALLOWED_ORIGINS in apps/edge-api/src/constants/cors.ts —
// the 2026-07-04 move to aelo.cloud updated that list but not this one, which
// 400'd every checkout, credit purchase and card setup in production (the UI
// sends `${window.location.origin}/…` = https://app.aelo.cloud/…).
// grow2.ai stays for staging + the dual-running legacy routes.
const ALLOWED_REDIRECT_DOMAINS = ['aelo.cloud', 'grow2.ai', 'localhost'];

export const safeRedirectUrl = z
  .string()
  .url()
  .refine((url) => {
    try {
      const { hostname } = new URL(url);
      return ALLOWED_REDIRECT_DOMAINS.some(
        (d) => hostname === d || hostname.endsWith(`.${d}`)
      );
    } catch {
      return false;
    }
  }, 'Redirect URL domain not allowed');

// ============================================================================
// Enums
// ============================================================================

export const subscriptionTierSchema = z.enum([
  'free',
  'starter',
  'pro',
  'business',
  'scale',
  'enterprise',
]);

export const subscriptionStatusSchema = z.enum([
  'active',
  'past_due',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'trialing',
  'unpaid',
  'paused',
]);

/**
 * Тот же статус, но как он ЧИТАЕТСЯ клиентом: известные значения плюс любое,
 * которое Stripe заведёт позже (`paused` появился в 2022-м и вряд ли последний).
 *
 * Не строгий enum, и это не послабление. Клиент валидирует ответ целиком
 * (`validatedRequest`), поэтому одно незнакомое значение роняло ВЕСЬ ответ, а
 * страница тарифов после этого рисовала free без подписки — то есть предлагала
 * купить тариф поверх живой подписки и выставить клиенту второй счёт. Цена
 * незнакомого статуса должна быть «не знаю, что это, считаю починимым», а не
 * второй счёт. Запросы по-прежнему проверяются строгим enum выше.
 */
export const subscriptionStatusResponseSchema = z.union([
  subscriptionStatusSchema,
  z.string(),
]);

/**
 * Код ответа, который клиент ПЕРЕВОДИТ, а не показывает как есть: подписки
 * больше нет, дальше — только покупка нового тарифа. Живёт здесь, потому что
 * это контракт между edge-api и ui-worker, и оба уже зависят от этого пакета;
 * два литерала по разные стороны разъехались бы молча (#890).
 */
export const SUBSCRIPTION_ENDED_CODE = 'subscription_ended';

// ============================================================================
// Checkout Schemas
// ============================================================================

export const createCheckoutRequestSchema = z
  .object({
    tier: subscriptionTierSchema.exclude(['free', 'enterprise']),
    successUrl: safeRedirectUrl,
    cancelUrl: safeRedirectUrl,
  })
  .strict();

/**
 * Accepted range for a wallet top-up, in cents ($5 – $10,000).
 *
 * Exported because the amount is entered by hand in the UI: a client-side bound
 * written as its own literal would drift from this schema and the mismatch would
 * only surface as a rejected checkout after the user has already typed a number.
 */
export const CREDIT_TOPUP_MIN_CENTS = 500;
export const CREDIT_TOPUP_MAX_CENTS = 1_000_000;

export const createCreditPurchaseRequestSchema = z
  .object({
    amountCents: z.number().int().min(CREDIT_TOPUP_MIN_CENTS).max(CREDIT_TOPUP_MAX_CENTS),
    successUrl: safeRedirectUrl,
    cancelUrl: safeRedirectUrl,
  })
  .strict();

export const checkoutResponseSchema = z.object({
  checkoutUrl: z.string().url(),
  sessionId: z.string(),
});

// ============================================================================
// Portal Schema
// ============================================================================

export const createPortalRequestSchema = z
  .object({
    // Same allowlist as every other redirect target: this value is handed to
    // Stripe as the portal's return destination, so an unconstrained URL turns
    // a Stripe-branded link into an open redirect. The only caller sends
    // window.location.href, so there is no off-domain use to preserve.
    returnUrl: safeRedirectUrl,
  })
  .strict();

export const portalResponseSchema = z.object({
  portalUrl: z.string().url(),
});

// ============================================================================
// Subscription Schemas
// ============================================================================

export const subscriptionResponseSchema = z.object({
  orgId: z.string(),
  tier: subscriptionTierSchema,
  status: subscriptionStatusResponseSchema,
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  // Set when a downgrade is scheduled for period end (current plan + minutes are
  // kept until then). null when there is no pending downgrade.
  pendingDowngrade: z
    .object({
      tier: subscriptionTierSchema,
      effectiveAt: z.string().nullable(),
    })
    .nullable(),
  limits: z.record(z.string(), z.number()),
  usage: z.object({
    usedMinutes: z.number(),
    includedMinutes: z.number(),
    overageMinutes: z.number(),
  }),
});

// ============================================================================
// Tier Limits Schemas
// ============================================================================

export const tierLimitSchema = z.object({
  limitKey: z.string(),
  limitValue: z.number(),
});

export const tierLimitsResponseSchema = z.object({
  tier: subscriptionTierSchema,
  limits: z.array(tierLimitSchema),
});

// ============================================================================
// Credit Purchase Schemas
// ============================================================================

export const creditPurchaseResponseSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string(),
  amountCents: z.number().int(),
  creditsMinutes: z.number().nullable(),
  status: z.string(),
  createdAt: z.string().nullable(),
});

export const creditPurchaseListResponseSchema = z.object({
  purchases: z.array(creditPurchaseResponseSchema),
  total: z.number().int(),
});

// ============================================================================
// Cancel / Reactivate Schemas
// ============================================================================

export const cancelSubscriptionResponseSchema = z.object({
  subscriptionId: z.string(),
  status: subscriptionStatusSchema,
  cancelAtPeriodEnd: z.boolean(),
  currentPeriodEnd: z.string().nullable(),
});

/**
 * Максимум для комментария при отмене. Типы Stripe SDK длину
 * `cancellation_details.comment` не декларируют, поэтому режем на своей
 * стороне: длинный текст не должен ронять саму отмену.
 */
export const CANCEL_COMMENT_MAX = 350;

/**
 * Причина отмены. Значения — родной enum Stripe
 * (`Subscription.CancellationDetails.Feedback`), чтобы причина попадала в
 * отчёты Stripe по оттоку, а не только в наш лог.
 */
export const cancelFeedbackSchema = z.enum([
  'customer_service',
  'low_quality',
  'missing_features',
  'other',
  'switched_service',
  'too_complex',
  'too_expensive',
  'unused',
]);

/**
 * Оба поля необязательны: отмена приходит и из портала Stripe, и от клиента
 * старой версии — там причины нет, и отмена обязана продолжать работать.
 */
export const cancelSubscriptionRequestSchema = z
  .object({
    feedback: cancelFeedbackSchema.optional(),
    comment: z.string().trim().max(CANCEL_COMMENT_MAX).optional(),
  })
  .strict();

export const reactivateSubscriptionResponseSchema = z.object({
  subscriptionId: z.string(),
  status: subscriptionStatusSchema,
  cancelAtPeriodEnd: z.boolean(),
  currentPeriodEnd: z.string().nullable(),
});

// ============================================================================
// Change Plan Schemas (in-app upgrade / downgrade)
// ============================================================================

export const changeSubscriptionRequestSchema = z
  .object({
    tier: subscriptionTierSchema.exclude(['free', 'enterprise']),
  })
  .strict();

export const changeSubscriptionResponseSchema = z.object({
  subscriptionId: z.string(),
  tier: subscriptionTierSchema,
  status: subscriptionStatusSchema,
  // 'now' for upgrades (immediate, prorated); 'period_end' for downgrades
  // (scheduled — current plan and its prepaid minutes are kept until then).
  effectiveAt: z.enum(['now', 'period_end']),
  currentPeriodEnd: z.string().nullable(),
});

/**
 * What an upgrade costs today, quoted by Stripe rather than estimated by us.
 *
 * `amountDueCents` is what leaves the card now (net of any account credit), so
 * a zero is a real answer — "nothing to pay today" — not a missing value.
 * Downgrades have no immediate charge and are not previewable here.
 */
export const previewSubscriptionChangeRequestSchema = z
  .object({
    tier: subscriptionTierSchema.exclude(['free', 'enterprise']),
  })
  .strict();

export const previewSubscriptionChangeResponseSchema = z.object({
  tier: subscriptionTierSchema,
  amountDueCents: z.number().int(),
  currency: z.string(),
});

// ============================================================================
// Webhook Schema (internal)
// ============================================================================

export const webhookResponseSchema = z.object({
  received: z.boolean(),
});

// ============================================================================
// Type exports
// ============================================================================

export type SubscriptionTier = z.infer<typeof subscriptionTierSchema>;
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;
export type CreateCheckoutRequest = z.infer<typeof createCheckoutRequestSchema>;
export type CreateCreditPurchaseRequest = z.infer<
  typeof createCreditPurchaseRequestSchema
>;
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;
export type CreatePortalRequest = z.infer<typeof createPortalRequestSchema>;
export type PortalResponse = z.infer<typeof portalResponseSchema>;
export type SubscriptionResponse = z.infer<typeof subscriptionResponseSchema>;
export type CreditPurchaseResponse = z.infer<typeof creditPurchaseResponseSchema>;
export type CancelSubscriptionResponse = z.infer<typeof cancelSubscriptionResponseSchema>;
export type CancelFeedback = z.infer<typeof cancelFeedbackSchema>;
export type CancelSubscriptionRequest = z.infer<typeof cancelSubscriptionRequestSchema>;
export type ReactivateSubscriptionResponse = z.infer<
  typeof reactivateSubscriptionResponseSchema
>;
export type ChangeSubscriptionRequest = z.infer<typeof changeSubscriptionRequestSchema>;
export type ChangeSubscriptionResponse = z.infer<typeof changeSubscriptionResponseSchema>;
export type PreviewSubscriptionChangeRequest = z.infer<
  typeof previewSubscriptionChangeRequestSchema
>;
export type PreviewSubscriptionChangeResponse = z.infer<
  typeof previewSubscriptionChangeResponseSchema
>;
